"use client";

import { useEffect, useRef, useState } from "react";
import { IconOut } from "./icons";
import { useRestored } from "./restored";
import { leave } from "@/components/auth/leave";
import { t, type Lang } from "@/lib/i18n/index";

/**
 * The way out, in the top bar beside day/night and the language: a menu with
 * one item, and the item signs out where it stands (Issue 260).
 *
 * It used to be a link to `/logout`, a page whose whole content was the
 * button that did it — a page load and a second press to say one thing. A
 * menu keeps what the page was for: one mis-click in the corner of every page
 * still only opens the menu, and ending the session takes the second,
 * deliberate press on a word that says so.
 *
 * Built like `LangToggle` beside it, because it is the same kind of thing:
 * Escape and a click outside close it, and the agent panel's own Escape
 * handler stands back while it is open (it looks for `aria-haspopup` with
 * `aria-expanded="true"`). Unlike it, focus moves: into the item when the
 * menu opens, and back to the icon when Escape closes it — otherwise Escape
 * hides the element that has focus and a keyboard is left on `<body>`.
 *
 * `going` is a ref and not only the `busy` state: two presses inside one
 * render would both see `busy` false, and two sign-outs is one request too
 * many. A request that fails lets go of it and says so in the menu, which
 * stays open for another try — the session is still there (`leave`).
 *
 * The back-forward cache brings back this page as it was left, which after a
 * sign-out is a menu stuck on 「请稍候…」; it is put back to closed and idle
 * (`useRestored`). Only the menu: the rest of the portal is not started over
 * the way the door is (auth/FreshOnReturn), because it is the page they came
 * back for.
 */
export function SignOutMenu({ lang }: { lang: Lang }) {
  const s = t(lang);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const going = useRef(false);
  const wrap = useRef<HTMLDivElement>(null);
  const icon = useRef<HTMLButtonElement>(null);
  const item = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    item.current?.focus();
    function away(e: MouseEvent) {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    }
    function key(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      setOpen(false);
      icon.current?.focus();
    }
    document.addEventListener("click", away);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("click", away);
      document.removeEventListener("keydown", key);
    };
  }, [open]);

  useRestored(() => {
    going.current = false;
    setBusy(false);
    setFailed(false);
    setOpen(false);
  });

  function toggle() {
    setFailed(false);
    setOpen((o) => !o);
  }

  async function out() {
    if (going.current) return;
    going.current = true;
    setBusy(true);
    setFailed(false);
    if (!(await leave())) {
      going.current = false;
      setBusy(false);
      setFailed(true);
    }
  }

  return (
    <div className={"signout" + (open ? " open" : "")} ref={wrap}>
      <button
        ref={icon}
        type="button"
        className="icon-btn"
        aria-haspopup="menu"
        data-track="shell.signout_open"
        aria-expanded={open}
        aria-label={s["auth.signOut"]}
        title={s["auth.signOut"]}
        onClick={toggle}
      >
        <IconOut />
      </button>
      <div className="signout-menu" role="menu" aria-label={s["auth.signOut"]}>
        <button ref={item} type="button" role="menuitem" data-track="shell.signout" onClick={out} disabled={busy}>
          {busy ? s["auth.working"] : s["auth.signOut"]}
        </button>
        {failed ? <p className="signout-note" role="alert">{s["auth.signOutFailed"]}</p> : null}
      </div>
    </div>
  );
}
